"use client";

import { LocaleLink } from "@i18n/routing";
import type { AiEffectsTool } from "@saas/shared/config/ai-effects-tools";
import { SECTION_TITLE } from "@shared/constants/typography";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { ArrowRightIcon, Wand2Icon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

export interface AiEffectsGridProps {
	tools: AiEffectsTool[];
	limit?: number;
	showMoreLink?: { href: string; label: string };
	showSectionHeader?: boolean;
	/** 标题区黑白（眉标灰、标题不渐变），侧栏 AI Effects 页用，和工作台一致；落地页里的保持品牌色 */
	neutralHeader?: boolean;
	className?: string;
}

export function AiEffectsGrid({
	tools,
	limit,
	showMoreLink,
	showSectionHeader = false,
	neutralHeader = false,
	className,
}: AiEffectsGridProps) {
	const t = useTranslations();
	const displayTools = limit != null ? tools.slice(0, limit) : tools;

	return (
		<div className={cn("flex flex-col gap-8", className)}>
			{showSectionHeader && (
				<div className="flex flex-col gap-4 text-center">
					<div
						className={cn(
							"flex items-center justify-center gap-2 font-semibold tracking-wide uppercase text-sm",
							neutralHeader ? "text-zinc-500" : "text-pink-600",
						)}
					>
						<Wand2Icon className="h-4 w-4" />
						<span>{t("aiEffectsPage.badge")}</span>
					</div>
					<h2
						className={cn(
							SECTION_TITLE.className,
							"text-4xl lg:text-5xl",
							!neutralHeader &&
								"bg-gradient-to-r from-pink-600 to-indigo-600 bg-clip-text text-transparent",
						)}
					>
						{t("aiEffectsPage.title")}
					</h2>
					<p className="text-lg text-muted-foreground max-w-2xl mx-auto px-4">
						{t("aiEffectsPage.description")}
					</p>
				</div>
			)}

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 max-w-7xl mx-auto w-full">
				{displayTools.map((tool) => (
					<div
						key={tool.translationKey}
						className="group relative bg-card rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 border border-border overflow-hidden flex flex-col hover:-translate-y-1"
					>
						<div className="p-6 flex flex-col flex-grow">
							<div className="mb-4">
								<h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-pink-600 transition-colors">
									{t(`${tool.translationKey}.title`)}
								</h3>
								<p className="text-muted-foreground text-sm leading-relaxed line-clamp-3 min-h-[4.5rem]">
									{t(`${tool.translationKey}.description`)}
								</p>
							</div>

							<div className="relative w-full aspect-video rounded-xl overflow-hidden bg-muted mb-6 border border-border group-hover:border-pink-200 transition-colors">
								{tool.beforeImage && tool.afterImage ? (
									<div className="relative w-full h-full flex items-center">
										<div className="relative w-1/2 h-full">
											<Image
												src={tool.beforeImage}
												alt="Before"
												fill
												className="object-cover"
												sizes="(max-width: 768px) 50vw, 33vw"
											/>
										</div>
										<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 bg-background/90 backdrop-blur-sm rounded-full p-2 shadow-lg border border-border">
											<ArrowRightIcon className="h-5 w-5 text-pink-600" />
										</div>
										<div className="relative w-1/2 h-full">
											<Image
												src={tool.afterImage}
												alt="After"
												fill
												className="object-cover"
												sizes="(max-width: 768px) 50vw, 33vw"
											/>
										</div>
									</div>
								) : null}
							</div>

							<div className="mt-auto">
								<Button
									variant="outline"
									className="w-full group/btn hover:bg-pink-600 hover:text-white transition-all duration-300"
									asChild
								>
									<LocaleLink href={tool.href}>
										{t("home.hero.tryNowButton")}
										{/* 让锚文本/无障碍名称带上目标页名字，同时不改变可见文案：
										    WCAG 2.5.3 要求无障碍名称包含可见文字，所以是追加而非覆盖。 */}
										<span className="sr-only">
											{t(`${tool.translationKey}.title`)}
										</span>
										<ArrowRightIcon className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
									</LocaleLink>
								</Button>
							</div>
						</div>
					</div>
				))}
			</div>

			{showMoreLink && (
				<div className="flex justify-center px-4">
					<Button variant="outline" className="group/btn" asChild>
						<LocaleLink href={showMoreLink.href}>
							{showMoreLink.label}
							{/* 「More」本身不描述目标页，补一段只给读屏器和爬虫的文字 */}
							<span className="sr-only">
								{t("aiEffectsPage.title")}
							</span>
							<ArrowRightIcon className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
						</LocaleLink>
					</Button>
				</div>
			)}
		</div>
	);
}
